// Visual progression through pitch stages. Inspired by Mochi's
// "17-Stage Conversation Flow" component, adapted to our pitch_stage taxonomy.

import { PITCH_STAGE_LABELS, PITCH_STAGE_ORDER } from "../lib/leadStatus";

interface Props {
  currentStage?: string | null;
  // Stages we've already passed through (from earlier drafts on this conversation)
  passedStages?: string[];
  // True when currentStage came from a manual pin rather than Charles's
  // own analysis. Renders a small "📌 pinned" indicator + an unpin
  // affordance.
  pinned?: boolean;
  // Click handler — when provided, stages become clickable. Click =
  // pin to that stage; clicking the currently-pinned stage unpins.
  onPinStage?: (stage: string | null) => void;
}

export function ConversationStepper({ currentStage, passedStages: _passedStages = [], pinned, onPinStage }: Props) {
  // passedStages used to feed a separate "visited" set, but that broke
  // when Louis manually pinned the conversation back to an earlier stage:
  // a stage Charles had touched in a prior draft (e.g. "objective") was
  // still rendered with the green checkmark even though we'd rolled back
  // to "dig". Now passed = strictly "stages whose index is before the
  // current one". Future stages — even if they appeared in older drafts
  // — render as future until we actually advance there again.
  const currentIdx = currentStage ? PITCH_STAGE_ORDER.indexOf(currentStage) : -1;
  const clickable = !!onPinStage;

  return (
    <div className="flex items-center gap-1 overflow-x-auto py-2">
      {PITCH_STAGE_ORDER.map((stage, idx) => {
        const isPassed = currentIdx >= 0 && idx < currentIdx;
        const isCurrent = stage === currentStage;
        const isFuture = !isPassed && !isCurrent;
        const handleClick = clickable
          ? () => {
              // Toggle: clicking the currently-pinned stage unpins; any
              // other click pins to that stage.
              if (pinned && isCurrent) onPinStage!(null);
              else onPinStage!(stage);
            }
          : undefined;
        return (
          <div key={stage} className="flex shrink-0 items-center">
            <button
              type="button"
              onClick={handleClick}
              disabled={!clickable}
              title={
                clickable
                  ? pinned && isCurrent
                    ? "Cliquer pour unpin"
                    : `Pin Charles à "${PITCH_STAGE_LABELS[stage] ?? stage}"`
                  : undefined
              }
              className={[
                "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium whitespace-nowrap transition-colors",
                clickable && "cursor-pointer hover:brightness-110",
                isCurrent &&
                  "bg-accent-500 text-white ring-2 ring-accent-500/40 shadow-glow",
                isPassed && !isCurrent && "bg-status-qualified/10 text-status-qualified ring-1 ring-status-qualified/30",
                isFuture && "bg-bg-elevated text-text-dim ring-1 ring-border",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {isPassed && !isCurrent && <span>✓</span>}
              {isCurrent && pinned && <span>📌</span>}
              {isCurrent && !pinned && <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-white"></span>}
              {PITCH_STAGE_LABELS[stage] ?? stage}
            </button>
            {idx < PITCH_STAGE_ORDER.length - 1 && (
              <div
                className={`mx-0.5 h-px w-3 ${
                  isPassed ? "bg-status-qualified/40" : "bg-border"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
